import { isDefinedError } from "@orpc/client"
import { AdminRole, SellerType } from "@repo/api-dtos"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import JsonView from "@uiw/react-json-view"
import { githubDarkTheme } from "@uiw/react-json-view/githubDark"
import { githubLightTheme } from "@uiw/react-json-view/githubLight"
import {
	Activity,
	ArrowLeft,
	Braces,
	Check,
	Copy,
	Crown,
	Network,
	ShoppingBag,
	Star,
} from "lucide-react"
import { useState } from "react"
import { useTheme } from "@/components/global/theme/theme-provider"
import { Panel, Section } from "@/components/layout"
import { AdminNotesPanel } from "@/components/marketplace"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
	type AccountAction,
	AccountStatusDialog,
} from "@/components/users/account-status-dialog"
import { ChangeEmailDialog } from "@/components/users/change-email-dialog"
import { FundHoldDialog } from "@/components/users/fund-hold-dialog"
import { NetworkTab } from "@/components/users/network/network-tab"
import { ProSellerTab } from "@/components/users/pro-seller-tab"
import { PurgeScammerDialog } from "@/components/users/purge-scammer-dialog"
import { RateLimitsDialog } from "@/components/users/rate-limits-dialog"
import { ShippingZonesDialog } from "@/components/users/shipping-zones-dialog"
import { UserActivityCard } from "@/components/users/user-activity-card"
import { UserCallouts } from "@/components/users/user-callouts"
import { formatCount } from "@/components/users/user-helpers"
import { UserInventoryCard } from "@/components/users/user-inventory-card"
import { UserKpiStrip } from "@/components/users/user-kpi-strip"
import { UserOffersCard } from "@/components/users/user-offers-card"
import { UserOrdersCard } from "@/components/users/user-orders-card"
import { UserPageHeader } from "@/components/users/user-page-header"
import { UserRail } from "@/components/users/user-rail"
import { UserRatingsCard } from "@/components/users/user-ratings-card"
import { UserReviewsTab } from "@/components/users/user-reviews-tab"
import { VerifiedSalesDialog } from "@/components/users/verified-sales-dialog"
import { AccountApiAccessPanel } from "@/features/api-data-access/AccountApiAccessPanel"
import { AccountUsageTab } from "@/features/api-usage/AccountUsageTab"
import type { PaletteCommand } from "@/features/command-palette/types"
import { useRegisterPaletteCommands } from "@/features/command-palette/use-register-palette-commands"
import { UserOrdersGrid } from "@/features/orders-admin/UserOrdersGrid"
import {
	ReferralSuspensionDialog,
	type ReferralSuspensionTarget,
} from "@/features/referrals-admin/ReferralSuspensionDialog"
import { useAdminRoles } from "@/hooks/use-admin-roles"
import {
	useAdminNotes,
	useAdminUserActivity,
	useAdminUserDetail,
	useAdminUserInventory,
	useAdminUserRatings,
} from "@/queries/marketplace"

const TABS = [
	"overview",
	"orders",
	"reviews",
	"pro-seller",
	"network",
	"api-usage",
	"json",
] as const
type UserTab = (typeof TABS)[number]

function isUserTab(value: unknown): value is UserTab {
	return (
		typeof value === "string" && (TABS as readonly string[]).includes(value)
	)
}

export const Route = createFileRoute("/users/$userId/")({
	component: UserDetailPage,
	validateSearch: (search: Record<string, unknown>): { tab?: UserTab } =>
		isUserTab(search.tab) ? { tab: search.tab } : {},
})

function UserDetailPage() {
	const { userId } = Route.useParams()
	const searchParams = Route.useSearch()
	const navigate = useNavigate()
	const { user, isLoading, error, refetch } = useAdminUserDetail({ userId })
	const { hasRole } = useAdminRoles()
	const canManageUsers = hasRole(AdminRole.MarketplaceUserManagement)
	const canViewApiUsage = hasRole(AdminRole.IntegrationsAdmin)
	const canAppealReviews = hasRole(AdminRole.MarketplaceOrderSupport)

	const setActiveTab = (tab: string) => {
		navigate({
			to: "/users/$userId",
			params: { userId },
			search: tab === "overview" ? {} : { tab: tab as UserTab },
			replace: true,
		})
	}

	const [notesOpen, setNotesOpen] = useState(false)
	const { total: notesTotal } = useAdminNotes({
		targetType: "user",
		targetId: user?.id ?? "",
		enabled: !!user,
	})

	const inventory = useAdminUserInventory({
		userId: user?.id ?? "",
		enabled: !!user,
	})
	const ratings = useAdminUserRatings({
		userId: user?.id ?? "",
		enabled: !!user,
	})
	const recentActivity = useAdminUserActivity({
		userId: user?.id ?? "",
		windowDays: 30,
		enabled: !!user && canManageUsers,
	})

	const [statusDialog, setStatusDialog] = useState<{
		open: boolean
		action: AccountAction | null
		scope: "seller" | "buyer"
	}>({ open: false, action: null, scope: "seller" })
	const [referralSuspensionMode, setReferralSuspensionMode] = useState<
		ReferralSuspensionTarget["mode"] | null
	>(null)
	const [fundHoldDialog, setFundHoldDialog] = useState<{
		open: boolean
		isActivating: boolean
	}>({ open: false, isActivating: true })
	const [rateLimitsOpen, setRateLimitsOpen] = useState(false)
	const [changeEmailOpen, setChangeEmailOpen] = useState(false)
	const [verifiedSalesOpen, setVerifiedSalesOpen] = useState(false)
	const [shippingZonesOpen, setShippingZonesOpen] = useState(false)
	const [purgeScammerOpen, setPurgeScammerOpen] = useState(false)

	useRegisterPaletteCommands("user-suspend", () => {
		if (!user || !canManageUsers) return []
		const commands: PaletteCommand[] = []
		if (
			user.isSeller &&
			(user.sellerAccountStatus?.status ?? "active") === "active"
		) {
			commands.push({
				id: "page:user:suspend-seller",
				label: "Suspend seller…",
				group: "page",
				requiredRoles: [AdminRole.MarketplaceUserManagement],
				perform: (ctx) => {
					openStatusDialog("suspend", "seller")
					ctx.close()
				},
			})
		}
		if ((user.buyerAccountStatus?.status ?? "active") === "active") {
			commands.push({
				id: "page:user:suspend-buyer",
				label: "Suspend buyer…",
				group: "page",
				requiredRoles: [AdminRole.MarketplaceUserManagement],
				perform: (ctx) => {
					openStatusDialog("suspend", "buyer")
					ctx.close()
				},
			})
		}
		return commands
	}, [user, canManageUsers])

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-4 sm:p-6">
				<Skeleton className="h-20 w-full" />
				<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
					<div className="space-y-4">
						<Skeleton className="h-40" />
						<Skeleton className="h-56" />
						<Skeleton className="h-56" />
					</div>
					<div className="space-y-4">
						<Skeleton className="h-48" />
						<Skeleton className="h-40" />
						<Skeleton className="h-64" />
					</div>
				</div>
			</div>
		)
	}

	if (error || !user) {
		const errorCode =
			error && "code" in error && typeof error.code === "string"
				? error.code
				: undefined
		const errorMessage = error?.message ?? "Unknown error"
		const notFound =
			!error || (isDefinedError(error) && errorCode === "USER_NOT_FOUND")
		return (
			<div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-4 sm:p-6">
				<h2 className="text-lg font-semibold">
					{notFound ? "User not found" : "Couldn't load this user"}
				</h2>
				<p className="text-bad">
					{notFound
						? `No user matches ${userId}.`
						: `${errorMessage}${errorCode ? ` (${errorCode})` : ""}`}
				</p>
				<div className="flex flex-wrap justify-center gap-2">
					<Button variant="outline" asChild>
						<Link to="/users" search={{} as never}>
							<ArrowLeft />
							Back to users
						</Link>
					</Button>
					<Button variant="outline" onClick={() => refetch()}>
						Retry
					</Button>
				</div>
			</div>
		)
	}

	const isPro = user.stripeConnect?.sellerType === SellerType.PRO
	const showNotes = notesOpen || (notesTotal ?? 0) > 0

	// A tab the URL asks for but this admin / this user cannot show falls back
	// to the overview instead of an empty column.
	const availableTabs: readonly UserTab[] = [
		"overview",
		"orders",
		"reviews",
		"json",
		...(isPro ? (["pro-seller"] as const) : []),
		...(canManageUsers ? (["network"] as const) : []),
		...(canViewApiUsage ? (["api-usage"] as const) : []),
	]
	const requestedTab = searchParams.tab ?? "overview"
	const activeTab: UserTab = availableTabs.includes(requestedTab)
		? requestedTab
		: "overview"

	const handleAddNote = () => {
		setNotesOpen(true)
		setActiveTab("overview")
		setTimeout(() => {
			document
				.getElementById("admin-notes")
				?.scrollIntoView({ behavior: "smooth", block: "center" })
		}, 50)
	}

	const openStatusDialog = (action: AccountAction, scope: "seller" | "buyer") =>
		setStatusDialog({ open: true, action, scope })

	const openFundHoldDialog = (isActivating: boolean) =>
		setFundHoldDialog({ open: true, isActivating })

	return (
		<div key={user.id} className="px-4 pb-10 sm:px-6">
			<UserPageHeader
				user={user}
				canManageUsers={canManageUsers}
				onStatusAction={openStatusDialog}
				onFundHoldAction={openFundHoldDialog}
				onChangeEmail={() => setChangeEmailOpen(true)}
				onEditRateLimits={() => setRateLimitsOpen(true)}
				onSetVerifiedSales={() => setVerifiedSalesOpen(true)}
				onEditShippingZones={() => setShippingZonesOpen(true)}
				onPurgeScammer={() => setPurgeScammerOpen(true)}
				onAddNote={handleAddNote}
			/>

			<div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="min-w-0">
					<Tabs
						value={activeTab}
						onValueChange={setActiveTab}
						className="gap-6"
					>
						<TabsList variant="line">
							<TabsTrigger value="overview">Overview</TabsTrigger>
							<TabsTrigger value="orders" className="flex items-center gap-2">
								<ShoppingBag />
								Orders
								<span className="text-xs text-meta tabular-nums">
									{user.orderActivity.buyer.total +
										user.orderActivity.seller.total}
								</span>
							</TabsTrigger>
							<TabsTrigger value="reviews" className="flex items-center gap-2">
								<Star />
								Reviews
								{ratings.data && (
									<span className="text-xs text-meta tabular-nums">
										{formatCount(
											ratings.data.seller.ledger.received +
												ratings.data.buyer.ledger.received,
										)}
									</span>
								)}
							</TabsTrigger>
							{isPro && (
								<TabsTrigger
									value="pro-seller"
									className="flex items-center gap-2"
								>
									<Crown />
									Pro seller
								</TabsTrigger>
							)}
							{canManageUsers && (
								<TabsTrigger
									value="network"
									className="flex items-center gap-2"
								>
									<Network />
									Network
								</TabsTrigger>
							)}
							{canViewApiUsage && (
								<TabsTrigger
									value="api-usage"
									className="flex items-center gap-2"
								>
									<Activity />
									API usage
								</TabsTrigger>
							)}
							<TabsTrigger value="json" className="flex items-center gap-2">
								<Braces />
								JSON
							</TabsTrigger>
						</TabsList>

						<TabsContent value="overview" className="mt-0">
							<div className="flex min-w-0 flex-col gap-4">
								<UserCallouts
									user={user}
									canManageUsers={canManageUsers}
									onStatusAction={openStatusDialog}
									onFundHoldAction={openFundHoldDialog}
									onViewProSeller={() => setActiveTab("pro-seller")}
									onEditShippingZones={() => setShippingZonesOpen(true)}
								/>
								<UserKpiStrip
									user={user}
									inventory={inventory.data}
									activity={recentActivity.data}
								/>
								{showNotes && (
									<div id="admin-notes" className="scroll-mt-28">
										<AdminNotesPanel
											targetType="user"
											targetId={user.id}
											variant="panel"
										/>
									</div>
								)}
								<UserActivityCard
									user={user}
									canViewAudit={canManageUsers}
									onViewNetwork={() => setActiveTab("network")}
								/>
								<UserOrdersCard
									user={user}
									onViewOrders={() => setActiveTab("orders")}
								/>
								<UserOffersCard
									userId={user.id}
									username={user.username}
									isSeller={user.isSeller}
								/>
								<UserRatingsCard
									user={user}
									onViewReviews={() => setActiveTab("reviews")}
								/>
								<UserInventoryCard
									user={user}
									breakdown={inventory.data}
									isLoading={inventory.isLoading}
									error={inventory.error}
								/>
							</div>
						</TabsContent>

						<TabsContent value="orders" className="mt-0">
							<UserOrdersGrid userId={user.id} username={user.username} />
						</TabsContent>

						<TabsContent value="reviews" className="mt-0">
							<UserReviewsTab user={user} canAppeal={canAppealReviews} />
						</TabsContent>

						{isPro && (
							<TabsContent value="pro-seller" className="mt-0">
								<ProSellerTab user={user} />
							</TabsContent>
						)}

						{canManageUsers && (
							<TabsContent value="network" className="mt-0">
								<NetworkTab userId={user.id} username={user.username} />
							</TabsContent>
						)}

						{canViewApiUsage && (
							<TabsContent value="api-usage" className="mt-0">
								<div className="flex flex-col gap-8">
									<AccountApiAccessPanel
										userId={user.id}
										username={user.username}
									/>
									<AccountUsageTab accountId={user.id} />
								</div>
							</TabsContent>
						)}

						<TabsContent value="json" className="mt-0">
							<Section
								label="Full user JSON"
								action={<CopyJsonButton data={user} />}
							>
								<Panel className="overflow-x-auto p-4">
									<UserJsonView data={user} />
								</Panel>
							</Section>
						</TabsContent>
					</Tabs>
				</div>

				<UserRail
					user={user}
					canManageUsers={canManageUsers}
					onEditRateLimits={() => setRateLimitsOpen(true)}
					onSetVerifiedSales={() => setVerifiedSalesOpen(true)}
					onReferralSuspension={(suspended) =>
						setReferralSuspensionMode(suspended ? "lift" : "suspend")
					}
				/>
			</div>

			<AccountStatusDialog
				open={statusDialog.open}
				onOpenChange={(open) => setStatusDialog((prev) => ({ ...prev, open }))}
				userId={user.id}
				username={user.username}
				action={statusDialog.action}
				scope={statusDialog.scope}
			/>
			{referralSuspensionMode ? (
				<ReferralSuspensionDialog
					target={{
						userId: user.id,
						username: user.username,
						mode: referralSuspensionMode,
					}}
					onClose={() => setReferralSuspensionMode(null)}
				/>
			) : null}
			<FundHoldDialog
				open={fundHoldDialog.open}
				onOpenChange={(open) =>
					setFundHoldDialog((prev) => ({ ...prev, open }))
				}
				userId={user.id}
				username={user.username}
				isActivating={fundHoldDialog.isActivating}
			/>
			<RateLimitsDialog
				open={rateLimitsOpen}
				onOpenChange={setRateLimitsOpen}
				userId={user.id}
				username={user.username}
			/>
			<ChangeEmailDialog
				open={changeEmailOpen}
				onOpenChange={setChangeEmailOpen}
				userId={user.id}
				username={user.username}
				currentEmail={user.email}
			/>
			<VerifiedSalesDialog
				open={verifiedSalesOpen}
				onOpenChange={setVerifiedSalesOpen}
				userId={user.id}
				username={user.username}
				verifiedSalesCount={user.verifiedSalesCount}
			/>
			{canManageUsers && user.sellerProfile && (
				<ShippingZonesDialog
					open={shippingZonesOpen}
					onOpenChange={setShippingZonesOpen}
					user={user}
				/>
			)}
			{canManageUsers && (
				<PurgeScammerDialog
					open={purgeScammerOpen}
					onOpenChange={setPurgeScammerOpen}
					userId={user.id}
					username={user.username}
				/>
			)}
		</div>
	)
}

function UserJsonView({ data }: { data: object }) {
	const { resolvedTheme } = useTheme()
	const theme = resolvedTheme === "dark" ? githubDarkTheme : githubLightTheme

	return (
		<JsonView
			value={data}
			style={theme}
			collapsed={2}
			displayDataTypes={false}
		/>
	)
}

function CopyJsonButton({ data }: { data: unknown }) {
	const [copied, setCopied] = useState(false)

	const handleCopy = async () => {
		await navigator.clipboard.writeText(JSON.stringify(data, null, 2))
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<Button variant="ghost" size="sm" onClick={handleCopy}>
			{copied ? <Check /> : <Copy />}
			{copied ? "Copied" : "Copy JSON"}
		</Button>
	)
}
